import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";

const SIDEBAR_WIDTH = 280;
const TOPBAR_HEIGHT = 64;

export default function Shell({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  // Prevent background scroll while drawer is open (mobile)
  useEffect(() => {
    const el = document.documentElement;
    if (open) el.style.overflow = "hidden";
    else el.style.overflow = "";
    return () => { el.style.overflow = ""; };
  }, [open]);

  return (
    <Root data-open={open ? "true" : "false"}>
      <Topbar onMenu={() => setOpen(true)} />
      <SidebarContainer>
        <Sidebar open={open} onClose={() => setOpen(false)} />
      </SidebarContainer>
      {open && <Backdrop onClick={() => setOpen(false)} aria-hidden />}
      <Main role="main">{children}</Main>
    </Root>
  );
}

const Root = styled.div`
  display: grid;
  grid-template-rows: ${TOPBAR_HEIGHT}px 1fr;
  grid-template-columns: 1fr;
  min-height: 100dvh;
  background: var(--bg);

  > header {
    grid-row: 1 / 2;
    grid-column: 1 / -1;
    z-index: 100;
  }

  @media (min-width: 1000px) {
    grid-template-columns: ${SIDEBAR_WIDTH}px 1fr;
  }
`;

const SidebarContainer = styled.div`
  @media (min-width: 1000px) {
    grid-row: 2 / 3;
    grid-column: 1 / 2;
    position: sticky;
    top: ${TOPBAR_HEIGHT}px;
    height: calc(100dvh - ${TOPBAR_HEIGHT}px);
    z-index: 40;
  }
`;

const Main = styled.main`
  grid-row: 2 / 3;
  grid-column: 1 / -1;
  padding-block: 16px;
  padding-inline: clamp(12px, 2vw, 24px);

  @media (min-width: 1000px) {
    grid-column: 2 / -1;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 39;
  animation: fadeIn 0.2s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @media (min-width: 1000px) { display: none; }
`;
